<template>
  <div class="columns is-marginless">
    <div class="column is-four-fifths is-offset-1">
      <div class="content create-member">
        <b-loading :active="loading" is-full-page />
        <router-link to="/member/list" class="create-member__prev-page">
          <i class="icon-chevron-left-bold" />
          返回人員管理
        </router-link>
        <h2 class="create-member__title">
          新增人員
          <span class="font-normal ml-4 text-gray-500 text-sm">
            新建立的帳號預設密碼為「a+公司統編」，例如：a54259841
          </span>
        </h2>
        <transition-group name="member-form">
          <member-form
            v-for="(member, index) in members"
            v-model="members[index]"
            :has-empty-department="false"
            :is-active="activeMemberIndex === index"
            :is-duplicated="!!memberAccountErrors[index]"
            :index="index"
            :key="`member-${member.index}`"
            @click="activeMemberIndex = index"
            @remove="removeMember(index)"
          />
        </transition-group>
        <b-button
          type="is-ghost"
          class="create-member__add-member"
          :disabled="loading"
          :loading="loading"
          @click="addMember">
          <i class="icon-plus mr-2" />
          添加其他人員
        </b-button>
        <div class="create-member__action">
          <w-button
            :disabled="loading || !v$.$anyDirty"
            class="mx-4"
            color-type="secondary"
            type="text"
            @click="reset"
          >
            取消
          </w-button>
          <w-button
            class="ml-6"
            :disabled="loading || v$.$invalid"
            :loading="loading"
            @click="submit"
          >
            新增
          </w-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import _cloneDeep from 'lodash/cloneDeep';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import MemberForm from '@/components/Member/MemberForm.vue';

import {
  NO_DEPARTMENT_ID,
  SNACKBAR_TYPES,
  USER_LEVEL,
} from '@/utils/constants';
import { INVITE_MEMBER } from '@/store/member/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';

const duplicatedAccount = (field) => helpers.withMessage(
  `${field}已經存在`,
  (value, sb, vm) => {
    const accounts = vm.members?.map(({ account } = {}) => account) || [];
    return !helpers.req(value) || accounts.filter((name) => name === value).length <= 1;
  },
);

export default {
  name: 'MemberCreate',
  components: {
    MemberForm,
  },
  data() {
    return {
      members: [],
      departments: [],
      activeMemberIndex: 0,
      loading: false,
    };
  },
  computed: {
    memberAccountErrors() {
      const error = this.v$.$silentErrors.filter((e) => e.$uid === 'members-$each');

      if (!error.length) {
        return [];
      }

      return error?.[0]?.$message?.map((e) => !!e.length) || [];
    },
    emptyMember() {
      return {
        account: '',
        department_id: this.departments[0]?.id,
        role: USER_LEVEL.NORMAL,
        member_detail: {
          name: '',
          serial: '',
          ext: '',
          phone: '',
          note: '',
        },
      };
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  async mounted() {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.loading = true;
      const departments = await this.getDepartmentList();
      this.departments = departments.filter((d) => d.id !== NO_DEPARTMENT_ID);
      if (!this.members.length) {
        this.addMember();
      }
    } catch {
      this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: '載入資料時發生錯誤，請重試一次' });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    getEmptyMember() {
      return _cloneDeep(this.emptyMember);
    },
    addMember() {
      const newMember = this.getEmptyMember();
      newMember.department_id = this.departments[0]?.id;
      newMember.index = this.members.length;
      newMember.allow_order_on_site = 0;
      this.members.push(newMember);
      this.activeMemberIndex = this.members.length - 1;
    },
    removeMember(index) {
      this.members.splice(index, 1);
    },
    reset() {
      const newMembers = this.getEmptyMember();
      this.members = [newMembers];
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.activeMemberIndex = 0;
      this.v$.$reset();
    },
    async submit() {
      try {
        this.loading = true;
        const members = this.members.map((member) => {
          const copy = _cloneDeep(member);
          if (copy.department_id === NO_DEPARTMENT_ID) {
            copy.department_id = null;
          }
          return copy;
        });
        await this.createMember({ members });
        this.$router.push('/member/list');
      } catch (ex) {
        const errorTitles = {
          9012: '帳號已存在',
          9013: '人員編號重複',
        };
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: errorTitles?.[ex.status] || '載入資料時發生錯誤，請重試一次', msg: ex.message || '' });
      } finally {
        this.loading = false;
      }
    },
    ...mapActions('member', {
      createMember: INVITE_MEMBER,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
  },
  validations() {
    return {
      members: {
        $each: helpers.forEach({
          account: {
            duplicatedAccount: duplicatedAccount('帳號'),
          },
        }),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.create-member {
  &__prev-page {
    font-size: .875rem;
    font-weight: 500;
    color: $dark-grey;
    i {
      vertical-align: middle;
    }
    &:hover {
      color: $blue;
      i::before {
        color: $blue;
      }
    }
  }
  &__title {
    margin: 1rem 0 !important;
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 40px;
  }
  &__add-member {
    margin-left: .5rem;
    &:hover {
      border: 1px solid $blue;
    }
    i::before {
      color: $blue;
    }
  }
  &__action {
    @apply flex items-center justify-end mt-4;
  }
}

.content {
  padding: 1rem;
  margin-top: 1rem;
  background-color: #fff;
  border-radius: 2px;
}

::v-deep .member-form {
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
  }
  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
}
</style>
